/**
 * GuideAvailabilityCalendar Component
 *
 * Interactive calendar grid for guide availability management.
 * Displays merged availability data with color-coded date cells.
 * Clicking a date emits onDateSelect(dateStr) to reveal detail in the right panel.
 *
 * SCRUM-821 Chunk B: AC-04 through AC-10
 * SCRUM-1135: Convert click-to-toggle → click-to-select (A-02)
 *
 * Color coding:
 * - White/default: Available
 * - Red: Manually blocked
 * - Blue: Blocked by repeating block
 * - Green with indicator: Manual override (available despite recurring block)
 *
 * @component
 * @param {Object} props
 * @param {Object} props.calendarData - Date-keyed availability data from useGuideAvailability
 * @param {string} props.currentMonth - ISO month string (YYYY-MM)
 * @param {boolean} props.loading - Whether data is being fetched
 * @param {Function} props.onDateSelect - Callback: (dateStr: string) => void — emitted on date click
 * @param {string|null} props.selectedDate - Currently selected date string (YYYY-MM-DD), or null
 */

import React, { useMemo } from 'react';
import { computeGroupClasses } from './utils/computeGroupClasses';

const DAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/**
 * Format a Date object to YYYY-MM-DD using local timezone (avoids UTC conversion issues)
 */
function toLocalDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function GuideAvailabilityCalendar({
  calendarData = {},
  currentMonth,
  loading = false,
  onDateSelect,
  selectedDate = null
}) {
  // Parse current month
  const [year, month] = currentMonth.split('-').map(Number);

  // Today's date string for comparisons
  const todayStr = useMemo(() => toLocalDateString(new Date()), []);

  // SCRUM-1152: Compute group position classes for connected date range rendering
  const groupClasses = useMemo(() => computeGroupClasses(calendarData), [calendarData]);

  // Compute grid days (current month + padding from prev/next months)
  const gridDays = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const days = [];

    // Padding from previous month (fill to start of week)
    const startDayOfWeek = firstDay.getDay();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      days.push(new Date(year, month - 1, -i));
    }

    // Days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month - 1, i));
    }

    // Padding from next month (fill to end of week)
    const lastDayOfWeek = lastDay.getDay();
    for (let i = 1; i <= 6 - lastDayOfWeek; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  }, [year, month]);

  const isCurrentMonth = (date) => {
    return date.getMonth() === month - 1 && date.getFullYear() === year;
  };

  const isPast = (dateStr) => dateStr < todayStr;

  const isToday = (dateStr) => dateStr === todayStr;

  const handleClick = (dateStr) => {
    if (isPast(dateStr) || loading) {
      return;
    }
    onDateSelect(dateStr);
  };

  /**
   * Get rounding class based on group position (SCRUM-1152)
   * Grouped dates get partial rounding; solo/ungrouped get default rounded-lg.
   */
  const getRoundingClass = (dateStr) => {
    const pos = groupClasses[dateStr];
    switch (pos) {
      case 'group-start':  return 'rounded-l-lg rounded-r-none';
      case 'group-middle': return 'rounded-none';
      case 'group-end':    return 'rounded-r-lg rounded-l-none';
      default:             return 'rounded-lg';
    }
  };

  /**
   * Get Tailwind classes for a date cell based on availability state
   */
  const getCellClassName = (dateStr, date) => {
    const info = calendarData[dateStr];
    const inCurrentMonth = isCurrentMonth(date);
    const past = isPast(dateStr);
    const today = isToday(dateStr);
    const rounding = getRoundingClass(dateStr);

    let base = `aspect-square flex items-center justify-center text-sm ${rounding} transition-colors relative `;

    // AC-09: Current day highlighted
    if (today && inCurrentMonth) {
      base += 'ring-2 ring-brand-primary ';
    }

    // Dates outside current month
    if (!inCurrentMonth) {
      return base + 'text-gray-300 cursor-default';
    }

    // AC-10: Past dates grayed out and not clickable
    if (past) {
      return base + 'text-gray-300 bg-gray-50 cursor-default opacity-[0.35]';
    }

    // Color by availability source (AC-05) + group type (SCRUM-1152)
    if (!info || info.available) {
      if (info?.manualOverride && info.available) {
        // Manual override — available despite recurring block (green with indicator)
        return base + 'bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 cursor-pointer font-medium';
      }
      // Default available
      return base + 'bg-white text-gray-900 hover:bg-gray-50 cursor-pointer border border-gray-100';
    }

    // SCRUM-1152: Booking group — brand-primary fill
    if (info.groupType === 'booking') {
      return base + 'bg-brand-primary text-white cursor-pointer';
    }

    // Blocked by repeating block (blue)
    if (info.source === 'recurring') {
      return base + 'bg-blue-50 text-blue-800 hover:bg-blue-100 cursor-pointer';
    }

    // Manually blocked (red) — SCRUM-1152: manual block groups get distinct styling via inline style
    return base + 'bg-red-50 text-red-800 hover:bg-red-100 cursor-pointer';
  };

  /**
   * Get accessible label for a date cell
   */
  const getAriaLabel = (date, dateStr) => {
    const info = calendarData[dateStr];
    const dateLabel = date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });

    if (isPast(dateStr)) {
      return `${dateLabel}, Past date`;
    }
    if (!info || info.available) {
      if (info?.manualOverride) {
        return `${dateLabel}, Available (manual override)`;
      }
      return `${dateLabel}, Available`;
    }
    if (info.source === 'recurring') {
      return `${dateLabel}, Blocked by repeating block${info.patternName ? ': ' + info.patternName : ''}`;
    }
    // SCRUM-1511: announce booking-blocked dates distinctly from manual blocks
    if (info.groupType === 'booking') {
      return `${dateLabel}, Confirmed booking`;
    }
    return `${dateLabel}, Manually blocked`;
  };

  // Loading skeleton (AC-04)
  if (loading) {
    return (
      <div className="grid grid-cols-7 gap-1" role="grid" aria-label="Availability calendar" aria-busy="true">
        {DAY_HEADERS.map(day => (
          <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2" role="columnheader">
            {day}
          </div>
        ))}
        {Array.from({ length: 35 }).map((_, i) => (
          <div key={`skeleton-${i}`} className="aspect-square rounded-lg bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-7 gap-1" role="grid" aria-label="Availability calendar">
      {/* Day-of-week headers (AC-04) */}
      {DAY_HEADERS.map(day => (
        <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2" role="columnheader">
          {day}
        </div>
      ))}

      {/* Date cells */}
      {gridDays.map((date, idx) => {
        const dateStr = toLocalDateString(date);
        const info = calendarData[dateStr];
        const inCurrentMonth = isCurrentMonth(date);
        const past = isPast(dateStr);

        const isSelected = selectedDate === dateStr && inCurrentMonth && !past;

        // SCRUM-1152: Manual block group colour treatment
        const groupPos = groupClasses[dateStr];
        const isManualGroup = info?.groupType === 'manual' && info?.blockGroupId;
        const isGroupEdge = groupPos === 'group-start' || groupPos === 'group-end';
        let cellStyle = isSelected
          ? { outline: '2px solid #2D5016', outlineOffset: '1px', borderRadius: '8px' }
          : undefined;
        if (isManualGroup && inCurrentMonth && !past) {
          cellStyle = {
            ...cellStyle,
            backgroundColor: '#FEE2E2',
            ...(isGroupEdge ? { border: '1px solid #EF4444' } : {})
          };
        }

        return (
          <button
            key={idx}
            onClick={() => {
              if (inCurrentMonth && !past) {
                handleClick(dateStr);
              }
            }}
            disabled={past || !inCurrentMonth || loading}
            role="gridcell"
            aria-label={getAriaLabel(date, dateStr)}
            className={getCellClassName(dateStr, date)}
            style={cellStyle}
          >
            {date.getDate()}

            {/* Repeating block indicator dot */}
            {info?.source === 'recurring' && !info.available && inCurrentMonth && !past && (
              <div
                className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-blue-500 rounded-full"
                aria-hidden="true"
              />
            )}

            {/* Manual override indicator dot */}
            {info?.manualOverride && info?.available && inCurrentMonth && !past && (
              <div
                className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-brand-primary rounded-full"
                aria-hidden="true"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
